import React, { useEffect, useState } from "react";
import { motion, useDragControls } from "framer-motion";
import { IconButton, styled } from "@mui/material";
import { ChevronLeftIcon } from "../../../atoms/icons/chevron-left";
import { ChevronRightIcon } from "../../../atoms/icons/chevron-right";

const CAROUSEL_ITEM_POSITIONS = {
  LEFT_OFF_SCREEN: "LEFT_OFF_SCREEN",
  LEFT: "LEFT",
  CENTER: "CENTER",
  RIGHT: "RIGHT",
  RIGHT_OFF_SCREEN: "RIGHT_OFF_SCREEN",
};

const CAROUSEL_ITEM_VARIANTS = {
  LEFT_OFF_SCREEN: {
    x: "-200%",
    scale: 0.8,
    zIndex: 0,
    opacity: 0.5,
    pointerEvents: "none",
  },
  LEFT: {
    x: "-86%",
    scale: 0.8,
    zIndex: 1,
    opacity: 0.8,
    pointerEvents: "none",
  },
  CENTER: { x: "0", scale: 1, zIndex: 2, opacity: 1 },
  RIGHT: {
    x: "86%",
    scale: 0.8,
    zIndex: 1,
    opacity: 0.8,
    pointerEvents: "none",
  },
  RIGHT_OFF_SCREEN: {
    x: "200%",
    scale: 0.8,
    zIndex: 0,
    opacity: 0.5,
    pointerEvents: "none",
  },
};

const StyledMotionDiv = styled("div")`
  grid-row-start: 1;
  grid-column-start: 1;
`;

export const CustomCarousel = ({ children }) => {
  const [ind, setInd] = useState(0);
  const dragControls = useDragControls();
  const [positions, setPositions] = useState(
    children.map((_, index) => {
      switch (index) {
        case 0:
          return CAROUSEL_ITEM_POSITIONS.CENTER;
        case 1:
          return CAROUSEL_ITEM_POSITIONS.RIGHT;
        default:
          return CAROUSEL_ITEM_POSITIONS.RIGHT_OFF_SCREEN;
      }
    })
  );
  const [centerIndex, setCenterIndex] = useState(0);

  const handleNext = (e) => {
    if (e) e.stopPropagation();
    const newPositions = [...positions];
    if (centerIndex > 0) {
      newPositions[centerIndex - 1] = CAROUSEL_ITEM_POSITIONS.LEFT_OFF_SCREEN;
    }
    newPositions[centerIndex] = CAROUSEL_ITEM_POSITIONS.LEFT;
    newPositions[centerIndex + 1] = CAROUSEL_ITEM_POSITIONS.CENTER;
    if (centerIndex + 2 < positions.length) {
      newPositions[centerIndex + 2] = CAROUSEL_ITEM_POSITIONS.RIGHT;
    }
    if (centerIndex + 3 < positions.length) {
      newPositions[centerIndex + 3] = CAROUSEL_ITEM_POSITIONS.RIGHT_OFF_SCREEN;
    }
    setCenterIndex(centerIndex + 1);
    setPositions(newPositions);
  };

  const handlePrevious = (e) => {
    if (e) e.stopPropagation();
    const newPositions = [...positions];
    if (centerIndex < positions.length - 1) {
      newPositions[centerIndex + 1] = CAROUSEL_ITEM_POSITIONS.RIGHT_OFF_SCREEN;
    }
    newPositions[centerIndex] = CAROUSEL_ITEM_POSITIONS.RIGHT;
    newPositions[centerIndex - 1] = CAROUSEL_ITEM_POSITIONS.CENTER;
    if (centerIndex - 2 >= 0) {
      newPositions[centerIndex - 2] = CAROUSEL_ITEM_POSITIONS.LEFT;
    }
    if (centerIndex - 3 >= 0) {
      newPositions[centerIndex - 3] = CAROUSEL_ITEM_POSITIONS.LEFT_OFF_SCREEN;
    }
    setCenterIndex(centerIndex - 1);
    setPositions(newPositions);
  };

  const handleDragEnd = (_, { offset }) => {
    if (offset.x > 100 && centerIndex !== 0) {
      handlePrevious();
    } else if (offset.x < -100 && centerIndex !== positions.length - 1) {
      handleNext();
    }
  };

  useEffect(() => {
    setInd(centerIndex);
  }, [centerIndex]);

  return (
    <div>
      <div className="carousel-flex-container">
        <IconButton
          aria-label="previous"
          size="small"
          disabled={centerIndex === 0}
          onClick={handlePrevious}
          sx={{
            marginRight: "-38px",
            zIndex: 3,
            "&.Mui-disabled": { display: "none" },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <motion.div
          drag="x"
          dragControls={dragControls}
          onDragEnd={handleDragEnd}
          dragSnapToOrigin
          dragTransition={{ bounceStiffness: 550 }}
          className="carousel-grid-container"
        >
          {children.map((child, index) => (
            <StyledMotionDiv
              key={child.key || index}
              animate={positions[index]}
              initial={CAROUSEL_ITEM_POSITIONS.CENTER}
              variants={CAROUSEL_ITEM_VARIANTS}
              transition={{ duration: 0.3, ease: "linear" }}
              as={motion.div}
            >
              {child}
            </StyledMotionDiv>
          ))}
        </motion.div>

        <IconButton
          aria-label="next"
          size="small"
          disabled={centerIndex === positions.length - 1}
          onClick={handleNext}
          sx={{
            marginLeft: "-38px",
            zIndex: 3,
            "&.Mui-disabled": { display: "none" },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </div>
      <div className="carousel-dots">
        {Array.from({ length: children.length }, (_, index) => (
          <div
            key={index}
            className={`dot ${index === ind ? "selected-dot" : ""}`}
          />
        ))}
      </div>
    </div>
  );
};
